import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  Button,
  Input,
  Divider,
  Link,
} from '@heroui/react';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { ThemeSwitcher } from '@/components/shared/ThemeSwitcher';
import TermsModal from '@/components/shared/TermsModal';
import PrivacyPolicyModal from '@/components/shared/PrivacyPolicyModal';
import LoginModal from '@/components/shared/LoginModal';
import macroLogo from '@/assets/logo-macroindustrial-tr.png';
import { resetPassword } from '@/services/empresa/users.service';

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [resetCode, setResetCode] = useState<string | null>(null);
  const [password, setPassword] = useState<string>('');
  const [confirmPassword, setConfirmPassword] = useState<string>('');
  const [isPasswordVisible, setIsPasswordVisible] = useState<boolean>(false);
  const [isConfirmPasswordVisible, setIsConfirmPasswordVisible] = useState<boolean>(false);
  const [isTermsModalOpen, setIsTermsModalOpen] = useState<boolean>(false);
  const [isPrivacyModalOpen, setIsPrivacyModalOpen] = useState<boolean>(false);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState<'success' | 'error'>('success');

  useEffect(() => {
    // Extract reset code from URL parameters
    const codeParam = searchParams.get('code');
    setResetCode(codeParam);
  }, [searchParams]);

  const handleResetPassword = async () => {
    if (!password || !confirmPassword) {
      setToastMessage('Por favor completa ambos campos de contraseña.');
      setToastType('error');
      setShowToast(true);
      setTimeout(() => setShowToast(false), 3000);
      return;
    }

    if (password !== confirmPassword) {
      setToastMessage('Las contraseñas no coinciden.');
      setToastType('error');
      setShowToast(true);
      setTimeout(() => setShowToast(false), 3000);
      return;
    }

    if (!resetCode) {
      setToastMessage('Código de restablecimiento inválido.');
      setToastType('error');
      setShowToast(true);
      setTimeout(() => setShowToast(false), 3000);
      return;
    }

    setIsLoading(true);
    try {
      await resetPassword(resetCode, password, confirmPassword);
      setToastMessage('Contraseña restablecida exitosamente.');
      setToastType('success');
      setShowToast(true);
      
      // Auto-navigate to login after 3 seconds
      setTimeout(() => {
        setShowToast(false);
        setIsLoginModalOpen(true); // Open login modal instead of navigating
      }, 3000);
      navigate('/')
    } catch (error) {
      console.error('Error resetting password:', error);
      setToastMessage('Error al restablecer la contraseña. El código puede ser inválido o haber expirado.');
      setToastType('error');
      setShowToast(true);
      setTimeout(() => setShowToast(false), 3000);
    } finally {
      setIsLoading(false);
    }
  };

  const togglePasswordVisibility = () => setIsPasswordVisible(!isPasswordVisible);
  const toggleConfirmPasswordVisibility = () => setIsConfirmPasswordVisible(!isConfirmPasswordVisible);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-800 dark:to-gray-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <Card className="shadow-xl bg-white dark:bg-gray-800">
          <CardHeader className="flex flex-col items-center py-8">
            <div className="w-full flex justify-end pr-4">
              <ThemeSwitcher />
            </div>
            <div className="flex justify-center mb-4">
              <img 
                src={macroLogo} 
                alt="MacroIndustrial" 
                className="h-16 object-contain"
              />
            </div>
            <h1 className="text-2xl font-bold text-gray-800 dark:text-white">Restablecer Contraseña</h1>
            <p className="text-gray-600 dark:text-gray-300 text-center mt-2">
              Ingresa tu nueva contraseña
            </p>
          </CardHeader>
          
          <Divider className="dark:bg-gray-700" />
          
          <CardBody className="py-6 px-8">
            <div className="space-y-4">
              <Input
                label="Nueva Contraseña"
                variant="bordered"
                type={isPasswordVisible ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                isRequired
                endContent={
                  <button 
                    className="focus:outline-none" 
                    type="button" 
                    onClick={togglePasswordVisibility}
                  >
                    {isPasswordVisible ? (
                      <EyeSlashIcon className="w-5 h-5 text-gray-500" />
                    ) : (
                      <EyeIcon className="w-5 h-5 text-gray-500" />
                    )}
                  </button>
                }
              />
              
              <Input
                label="Confirmar Contraseña"
                variant="bordered"
                type={isConfirmPasswordVisible ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                isRequired
                endContent={
                  <button 
                    className="focus:outline-none" 
                    type="button" 
                    onClick={toggleConfirmPasswordVisibility}
                  >
                    {isConfirmPasswordVisible ? (
                      <EyeSlashIcon className="w-5 h-5 text-gray-500" />
                    ) : (
                      <EyeIcon className="w-5 h-5 text-gray-500" />
                    )}
                  </button>
                }
              />
              
              {resetCode && (
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 mt-2">
                  <p className="text-xs text-blue-700 dark:text-blue-300">
                    Código de restablecimiento: {resetCode.substring(0, 8)}...{resetCode.substring(resetCode.length - 8)}
                  </p>
                </div>
              )}
            </div>
            
            {resetCode ? (
              <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 mt-4">
                <div className="flex items-center">
                  <svg className="w-5 h-5 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                  </svg>
                  <p className="text-green-700 dark:text-green-300 text-sm">
                    Código de restablecimiento válido. Ingresa tu nueva contraseña.
                  </p>
                </div>
              </div>
            ) : (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mt-4">
                <div className="flex items-center">
                  <svg className="w-5 h-5 text-red-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                  </svg>
                  <p className="text-red-700 dark:text-red-300 text-sm">
                    Código de restablecimiento no encontrado. Asegúrate de usar el enlace completo desde tu correo.
                  </p>
                </div>
              </div>
            )}
          </CardBody>
          
          <CardFooter className="flex flex-col py-6 px-8">
            <Button 
              variant="solid"
              onPress={handleResetPassword}
              className="w-full"
              isLoading={isLoading}
              isDisabled={isLoading || !resetCode || !password || !confirmPassword}
            >
              {isLoading ? 'Restableciendo...' : 'Restablecer Contraseña'}
            </Button>
            
            <p className="text-xs text-gray-500 mt-4 text-center dark:text-gray-400">
              By signing up or signing in, you agree to our{' '}
              <Link 
                className="text-xs text-blue-500 hover:underline cursor-pointer"
                onClick={(e) => {
                  e.preventDefault();
                  setIsTermsModalOpen(true);
                }}
              >
                Terms
              </Link>{' '}
              and{' '}
              <Link 
                className="text-xs text-blue-500 hover:underline cursor-pointer"
                onClick={(e) => {
                  e.preventDefault();
                  setIsPrivacyModalOpen(true);
                }}
              >
                Privacy Policy
              </Link>.
            </p>
          </CardFooter>
        </Card>
        
        {/* Login Modal */}
        <LoginModal 
          isOpen={isLoginModalOpen} 
          onClose={() => setIsLoginModalOpen(false)} 
        />
        
        {/* Terms Modal */}
        <TermsModal 
          isOpen={isTermsModalOpen} 
          onClose={() => setIsTermsModalOpen(false)} 
        />
        
        {/* Privacy Policy Modal */}
        <PrivacyPolicyModal 
          isOpen={isPrivacyModalOpen} 
          onClose={() => setIsPrivacyModalOpen(false)} 
        />
        
        {/* Toast Notification */}
        {showToast && (
          <div 
            className={`fixed top-4 right-4 z-50 p-4 rounded-md shadow-lg transition-opacity duration-300 ${
              toastType === 'error' 
                ? 'bg-red-500 text-white' 
                : 'bg-green-500 text-white'
            }`}
          >
            <div className="flex items-center">
              <span>{toastMessage}</span>
              <button 
                onClick={() => setShowToast(false)}
                className="ml-4 text-white hover:text-gray-200 focus:outline-none"
              >
                ×
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ResetPassword;